import { SelectableEnumerationItem } from "src/app/core/models/lookup.model";
import {
  getScheduleVisitTypeDisplayLabel,
  resolveScheduleVisitTypeSlug,
  SCHEDULE_VISIT_TYPE_OPTIONS,
} from "src/app/core/models/schedule-visit-types";

/**
 * Visit types shown on Schedule / Worklist facet filters beyond what counts API may return.
 */
const SUPPLEMENTAL_VISIT_TYPES: { displayName: string; value: string }[] =
  SCHEDULE_VISIT_TYPE_OPTIONS.map((o) => ({
    displayName: o.displayName,
    value: o.value,
  }));

function visitTypeFacetKey(value: string): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

/** Map referrals/counts facet label to canonical slug + display label. */
export function mapVisitTypeFacetFromApiLabel(
  label: string,
  isSelected: boolean
): SelectableEnumerationItem {
  const slug =
    resolveScheduleVisitTypeSlug(label) ||
    String(label ?? "")
      .trim()
      .toLowerCase();
  return {
    displayName: getScheduleVisitTypeDisplayLabel(slug || label),
    value: slug,
    isSelected,
  };
}

/** Merges API/counts-derived visit-type rows with canonical visit types; preserves selection flags. */
export function mergeWorksheetVisitTypeFacetRows(
  rows: SelectableEnumerationItem[] | null | undefined
): SelectableEnumerationItem[] {
  const out = new Map<string, SelectableEnumerationItem>();
  for (const r of rows ?? []) {
    const slug =
      resolveScheduleVisitTypeSlug(String(r.value ?? r.displayName ?? "")) ||
      String(r.value ?? "")
        .trim()
        .toLowerCase();
    const k = visitTypeFacetKey(slug);
    if (!k || k === "all") {
      continue;
    }
    out.set(k, {
      ...r,
      value: slug,
      displayName: getScheduleVisitTypeDisplayLabel(slug || String(r.displayName ?? "")),
      isSelected: !!r.isSelected,
    });
  }
  for (const s of SUPPLEMENTAL_VISIT_TYPES) {
    const k = visitTypeFacetKey(s.value);
    const existing = out.get(k);
    if (existing) {
      out.set(k, {
        ...existing,
        displayName: s.displayName,
        value: s.value,
      });
    } else {
      out.set(k, {
        displayName: s.displayName,
        value: s.value,
        isSelected: false,
      });
    }
  }
  const order = new Map(
    SUPPLEMENTAL_VISIT_TYPES.map((s, index) => [visitTypeFacetKey(s.value), index])
  );
  return [...out.values()].sort((a, b) => {
    const ai = order.get(visitTypeFacetKey(String(a.value ?? ""))) ?? 999;
    const bi = order.get(visitTypeFacetKey(String(b.value ?? ""))) ?? 999;
    return ai - bi;
  });
}
